import { useLocation, useRouter } from "@tanstack/react-router"
import { Box, Briefcase, Wallet1 } from "iconsax-reactjs"
import React, { type ReactNode } from "react"

import { CampaignIcon } from "@/components/shared"
import { Image } from "@/components/ui"
import { EFolderAssets } from "@/modules/helpers/enum"
import { useCurrentUser, useLayoutStore } from "@/modules/helpers/hooks/shared"
import { cn } from "@/modules/helpers/hooks/shared/utils"
import { getImageByNameFromS3, sendHaptic } from "@/modules/helpers/utils"

interface AvatarIconProps {
  size?: number
  color?: string
}

const AvatarIcon: React.FC<AvatarIconProps> = React.memo(({ size = 20, color = "#636CCB" }) => {
  const user = useCurrentUser((state) => state.user)
  const _hasHydrated = useCurrentUser((state) => state._hasHydrated)

  const avatarSrc = _hasHydrated
    ? getImageByNameFromS3(user?.avatarName, user?.id, EFolderAssets.AVATAR)
    : "/favicon-96x96.png"

  return (
    <Image
      src={avatarSrc}
      alt={"AVATAR"}
      width={size}
      height={size}
      className={cn("w-5 h-5 rounded-full border")}
      style={{ borderColor: color }}
    />
  )
})

AvatarIcon.displayName = "AvatarIcon"

interface NavigationItemProps {
  renderIcon: (active: boolean) => ReactNode
  title: string
  path: string
  className?: string
  location: string
  key: string

  "data-tour"?: string
}
const NavigationItem: React.FC<NavigationItemProps> = ({
  renderIcon,
  path,
  title,
  className,
  location,
  "data-tour": dataTour
}) => {
  const active = location === path
  const router = useRouter()

  const onClick = () => {
    sendHaptic()
    router.navigate({
      to: path,
      replace: true
    })
  }

  return (
    <div
      data-tour={dataTour}
      className={cn(
        `active:bg-gray-100 rounded-lg lg:rounded-none lg:active:bg-gray-100/40 transition-all ease-in duration-100 p-2 flex-1 cursor-pointer justify-center flex flex-col items-center gap-1 lg:border-gray-100/10`,
        {
          "lg:border-primary": active,
          [className as string]: className
        }
      )}
      onClick={onClick}
    >
      <div className="w-5 h-5">{renderIcon(active)}</div>
      <p
        className={cn(
          "!text-xss text-center font-normal my-1 text-black transition-transform duration-200",
          {
            "text-primary-800": active
          }
        )}
      >
        {title}
      </p>
    </div>
  )
}
export const NavigationBottom = ({ className = "" }) => {
  const location = useLocation()
  const { insetBottom } = useLayoutStore()

  const routers = React.useMemo(
    () => [
      {
        title: "Chiến dịch",
        path: "/",
        icon: CampaignIcon,
        keyUnique: "explore"
      },
      {
        title: "Sản phẩm",
        path: "/affiliate",
        icon: Box,
        keyUnique: "affiliate"
      },
      {
        title: "Quản lý",
        path: "/manage",
        icon: Briefcase,
        keyUnique: "manage"
      },
      {
        title: "Số dư",
        path: "/billing",
        icon: Wallet1,
        keyUnique: "billing"
      },
      {
        title: "Cá nhân",
        path: "/profile",
        icon: AvatarIcon,
        keyUnique: "profile"
      }
    ],
    []
  )

  const activeIndex = routers.findIndex((r) => r.path === location.pathname)

  return (
    <div
      className={cn("sticky bottom-0 z-10 bg-white flex w-full justify-between shadow", className)}
      style={{
        paddingBottom: `${insetBottom + 4}px`
      }}
    >
      {/* indicator chạy qua lại */}
      <span
        className="absolute top-0 h-[3px] bg-gradient-to-r from-primary to-secondary transition-transform duration-300 ease-in-out"
        style={{
          width: `${100 / routers.length}%`,
          transform: `translateX(${activeIndex * 100}%)`
        }}
      />

      {routers.map((item) => (
        <NavigationItem
          {...item}
          key={item.keyUnique}
          location={location.pathname}
          renderIcon={(active) => {
            const IconComponent = item.icon as React.ComponentType<{
              size?: number
              color?: string
            }>
            return <IconComponent size={21} color={active ? "#045664" : "#000"} />
          }}
          data-tour={`${item.keyUnique}-tab`}
        />
      ))}
    </div>
  )
}
